import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';
import PageBackground from '../components/PageBackground';
import EntityDetailModal from '../components/EntityDetailModal';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Check, Info } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function MotoTeamSelection() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [riders, setRiders] = useState([]);
  const [bikes, setBikes] = useState([]);
  const [tuners, setTuners] = useState([]);
  const [selectedRiders, setSelectedRiders] = useState([]);
  const [selectedBikes, setSelectedBikes] = useState([]);
  const [selectedTuner, setSelectedTuner] = useState(null);
  const [teamName, setTeamName] = useState('');
  const [showNameModal, setShowNameModal] = useState(false);
  const [hasExistingSelection, setHasExistingSelection] = useState(false);
  const [budget] = useState(150000);
  const [spentCredits, setSpentCredits] = useState(0);
  const [selectionWindowOpen, setSelectionWindowOpen] = useState(true);
  const [selectionWindowMessage, setSelectionWindowMessage] = useState('');
  const [settings, setSettings] = useState({});
  const [detailModal, setDetailModal] = useState({ isOpen: false, entity: null, type: null });

  const fetchData = useCallback(async () => {
    try {
      const [userRes, selectionRes, ridersRes, bikesRes, tunersRes, settingsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/moto/my-selection`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/moto/riders`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/moto/bikes`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/moto/tuners`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/system/settings`, { withCredentials: true }),
      ]);

      setUser(userRes.data);
      setRiders(ridersRes.data);
      setBikes(bikesRes.data);
      setTuners(tunersRes.data);
      setSettings(settingsRes.data);

      const hasSelection = selectionRes.data?.riders?.length > 0;
      const adminWindowOpen = settingsRes.data?.moto_selection_window_open ?? true;

      // Check if we're in the transfer window (July 6 - August 29)
      const now = new Date();
      const currentYear = now.getFullYear();
      const transferStart = new Date(currentYear, 6, 6); // July 6 (month is 0-indexed)
      const transferEnd = new Date(currentYear, 7, 29, 23, 59, 59); // August 29
      const inTransferWindow = now >= transferStart && now <= transferEnd;

      // User can modify if: no existing selection OR (has selection AND in transfer window AND admin hasn't locked)
      const canModify = !hasSelection || (inTransferWindow && adminWindowOpen);

      setSelectionWindowOpen(canModify);
      setHasExistingSelection(hasSelection);

      if (hasSelection && !canModify) {
        setSelectionWindowMessage('Team changes are locked. You can only modify your lineup during the transfer window (July 6 - August 29).');
      }

      if (selectionRes.data?.riders?.length > 0) {
        setSelectedRiders(selectionRes.data.riders);
        setSelectedBikes(selectionRes.data.bikes);
        setSelectedTuner(selectionRes.data.tuner);
        if (selectionRes.data.team_name) setTeamName(selectionRes.data.team_name);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load data');
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Recompute spent credits whenever selections or catalog data change.
  useEffect(() => {
    let total = 0;

    selectedRiders.forEach(riderId => {
      const rider = riders.find(r => r.rider_id === riderId);
      if (rider) total += rider.price || 0;
    });

    selectedBikes.forEach(bikeId => {
      const bike = bikes.find(b => b.bike_id === bikeId);
      if (bike) total += bike.price || 0;
    });

    if (selectedTuner) {
      const tuner = tuners.find(t => t.tuner_id === selectedTuner);
      if (tuner) total += tuner.price || 0;
    }

    setSpentCredits(total);
  }, [selectedRiders, selectedBikes, selectedTuner, riders, bikes, tuners]);

  const toggleRider = (riderId) => {
    if (hasExistingSelection && !selectionWindowOpen) {
      toast.error('Team is locked. Changes only allowed during transfer window (July 6 - August 29).');
      return;
    }
    setSelectedRiders((prev) => {
      if (prev.includes(riderId)) {
        return prev.filter((id) => id !== riderId);
      } else if (prev.length < 2) {
        return [...prev, riderId];
      } else {
        toast.error('Maximum 2 riders allowed');
        return prev;
      }
    });
  };

  const toggleBike = (bikeId) => {
    if (hasExistingSelection && !selectionWindowOpen) {
      toast.error('Team is locked. Changes only allowed during transfer window (July 6 - August 29).');
      return;
    }
    setSelectedBikes((prev) => {
      if (prev.includes(bikeId)) {
        return prev.filter((id) => id !== bikeId);
      } else if (prev.length < 3) {
        return [...prev, bikeId];
      } else {
        toast.error('Maximum 3 bikes allowed');
        return prev;
      }
    });
  };

  const selectTuner = (tunerId) => {
    if (hasExistingSelection && !selectionWindowOpen) {
      toast.error('Team is locked. Changes only allowed during transfer window (July 6 - August 29).');
      return;
    }
    setSelectedTuner(tunerId);
  };

  const handleSubmit = async () => {
    if (selectedRiders.length !== 2) {
      toast.error('Please select exactly 2 riders');
      return;
    }
    if (selectedBikes.length !== 3) {
      toast.error('Please select exactly 3 bikes');
      return;
    }
    if (!selectedTuner) {
      toast.error('Please select 1 tuner');
      return;
    }
    
    if (spentCredits > budget) {
      toast.error(`Budget exceeded! You've spent ${spentCredits.toLocaleString()} credits but limit is ${budget.toLocaleString()}`);
      return;
    }

    // Check if this save will incur a -50 point penalty for changing
    // an existing selection within the current round window.
    let willIncurPenalty = false;
    let penaltyAmount = 0;
    if (hasExistingSelection) {
      try {
        const previewResp = await axios.get(
          `${BACKEND_URL}/api/my-selection/penalty-preview?league=moto`,
          { withCredentials: true }
        );
        willIncurPenalty = !!previewResp.data?.will_incur_penalty;
        penaltyAmount = previewResp.data?.penalty_amount || 0;
      } catch (e) {
        console.warn('Penalty preview failed, proceeding without warning', e);
      }

      if (willIncurPenalty) {
        const confirmed = window.confirm(
          `Heads up: changing your moto team this round will cost you ${penaltyAmount} points. Subsequent edits in the same round are free. Continue?`
        );
        if (!confirmed) return;
      }
    }

    try {
      await axios.post(
        `${BACKEND_URL}/api/moto/my-selection`,
        {
          riders: selectedRiders,
          bikes: selectedBikes,
          tuner: selectedTuner,
          team_name: teamName.trim() || undefined,
        },
        { withCredentials: true }
      );

      if (hasExistingSelection) {
        if (willIncurPenalty) {
          toast.warning(`Team updated! ${penaltyAmount} points penalty applied for editing your team this round.`);
        } else {
          toast.success('Team updated! No penalty (already edited this round).');
        }
        setTimeout(() => navigate('/dashboard'), 2000);
      } else {
        toast.success('Moto Team selected successfully!');
        setShowNameModal(true);
      }
    } catch (error) {
      console.error('Failed to save selection:', error);
      toast.error(error.response?.data?.detail || 'Failed to save selection');
    }
  };

  return (
    <PageBackground pageKey="moto_selection">
    <div className="min-h-screen">
      <Navbar user={user} />

      {/* Budget Display - Sticky - Must be outside scrollable container */}
      <div className="sticky top-16 z-40 bg-background/95 backdrop-blur-sm py-3 border-b border-zinc-800/50" data-testid="moto-sticky-budget-bar">
        <div className="max-w-7xl mx-auto px-4">
          <div className="bg-card border border-zinc-800 p-4 inline-block">
            <div className="flex items-center space-x-8">
              <div>
                <p className="text-xs text-zinc-400 mb-0.5">Total Budget</p>
                <p className="font-mono text-2xl font-bold text-white">{budget.toLocaleString()}</p>
              </div>
              <div>
                <p className="text-xs text-zinc-400 mb-0.5">Spent</p>
                <p className={`font-mono text-2xl font-bold ${spentCredits > budget ? 'text-red-500' : 'text-secondary'}`}>
                  {spentCredits.toLocaleString()}
                </p>
              </div>
              <div>
                <p className="text-xs text-zinc-400 mb-0.5">Remaining</p>
                <p className={`font-mono text-2xl font-bold ${budget - spentCredits < 0 ? 'text-red-500' : 'text-primary'}`}>
                  {(budget - spentCredits).toLocaleString()}
                </p>
              </div>
            </div>
            {spentCredits > budget && (
              <p className="text-red-500 text-sm mt-2 font-bold">Budget exceeded! Reduce your selections.</p>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="mb-8">
          <div className="inline-block bg-secondary/20 text-secondary px-3 py-1 text-sm font-bold mb-4">
            MSF Moto
          </div>
          <h1 className="font-heading font-black text-5xl mb-2" data-testid="moto-team-selection-title">
            BUILD YOUR <span className="text-secondary">GARAGE</span>
          </h1>
          <p className="text-zinc-400 mb-4">
            Select 2 riders, 3 bikes, and 1 tuner. Changes incur a 50-point penalty.
          </p>
          
          {/* Selection Window Warning */}
          {hasExistingSelection && !selectionWindowOpen && (
            <div className="bg-red-900/20 border border-red-500 p-4 mb-6" data-testid="moto-selection-locked-warning">
              <p className="text-red-400 font-bold">🔒 Selection Locked</p>
              <p className="text-red-300 text-sm mt-1">{selectionWindowMessage}</p>
            </div>
          )}
        </div>

        <div className="space-y-6">
          <Accordion type="single" collapsible defaultValue={!hasExistingSelection || selectionWindowOpen ? "riders" : undefined} className="space-y-4">
            <AccordionItem value="riders" className="bg-card border border-zinc-800" disabled={hasExistingSelection && !selectionWindowOpen}>
              <AccordionTrigger className="px-6 py-4 hover:no-underline" data-testid="accordion-riders">
                <div className="flex items-center justify-between w-full pr-4">
                  <h2 className="font-heading font-bold text-3xl">
                    SELECT <span className="text-secondary">RIDERS</span>
                  </h2>
                  <span className="font-mono text-xl text-zinc-400">
                    {selectedRiders.length}/2
                  </span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-3 sm:px-6 pb-6">
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-3 sm:gap-4 pt-4">
                  {riders.map((rider) => {
                    const isSelected = selectedRiders.includes(rider.rider_id);
                    return (
                      <div
                        key={rider.rider_id}
                        data-testid={`rider-card-${rider.rider_id}`}
                        className={`relative bg-zinc-900 border-2 transition-all ${
                          isSelected ? 'border-secondary' : 'border-zinc-800 hover:border-zinc-600'
                        } p-3 sm:p-6`}
                        style={isSelected ? { boxShadow: '0 0 20px rgba(0, 255, 135, 0.3)' } : {}}
                      >
                        {isSelected && (
                          <div className="absolute top-2 right-2 bg-secondary rounded-full p-1">
                            <Check size={20} className="text-black" />
                          </div>
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setDetailModal({ isOpen: true, entity: rider, type: 'rider' });
                          }}
                          className="absolute top-2 left-2 bg-zinc-800 hover:bg-zinc-700 rounded-full p-1.5 transition-colors"
                          title="View Details"
                        >
                          <Info size={16} className="text-zinc-400" />
                        </button>
                        <div 
                          onClick={() => setDetailModal({ isOpen: true, entity: rider, type: 'rider' })}
                          className="cursor-pointer"
                        >
                          {rider.image_url ? (
                            <img
                              src={rider.image_url}
                              alt={rider.name}
                              className="w-full h-32 sm:h-48 object-cover mb-3 sm:mb-4"
                            />
                          ) : (
                            <div className="w-full h-32 sm:h-48 bg-zinc-800 flex items-center justify-center mb-3 sm:mb-4">
                              <span className="font-heading font-bold text-3xl sm:text-5xl text-zinc-600">{rider.name?.charAt(0) || 'R'}</span>
                            </div>
                          )}
                          <h3 className="font-heading font-bold text-base sm:text-xl mb-1 truncate">{rider.name}</h3>
                          <p className="text-xs text-zinc-500 mt-1 truncate">{rider.nationality}</p>
                          <div className="mt-2 sm:mt-3 pt-2 sm:pt-3 border-t border-zinc-700">
                            <p className="font-mono text-sm sm:text-lg font-bold text-secondary">{(rider.price || 0).toLocaleString()} CR</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="bikes" className="bg-card border border-zinc-800">
              <AccordionTrigger className="px-6 py-4 hover:no-underline" data-testid="accordion-bikes">
                <div className="flex items-center justify-between w-full pr-4">
                  <h2 className="font-heading font-bold text-3xl">
                    SELECT <span className="text-secondary">BIKES</span>
                  </h2>
                  <span className="font-mono text-xl text-zinc-400">
                    {selectedBikes.length}/3
                  </span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-3 sm:px-6 pb-6">
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-3 sm:gap-4 pt-4">
                  {bikes.map((bike) => {
                    const isSelected = selectedBikes.includes(bike.bike_id);
                    return (
                      <div
                        key={bike.bike_id}
                        data-testid={`bike-card-${bike.bike_id}`}
                        className={`relative bg-zinc-900 border-2 transition-all ${
                          isSelected ? 'border-secondary' : 'border-zinc-800 hover:border-zinc-600'
                        } p-3 sm:p-6`}
                        style={isSelected ? { boxShadow: '0 0 20px rgba(0, 255, 135, 0.3)' } : {}}
                      >
                        {isSelected && (
                          <div className="absolute top-2 right-2 bg-secondary rounded-full p-1">
                            <Check size={20} className="text-black" />
                          </div>
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setDetailModal({ isOpen: true, entity: bike, type: 'bike' });
                          }}
                          className="absolute top-2 left-2 bg-zinc-800 hover:bg-zinc-700 rounded-full p-1.5 transition-colors"
                          title="View Details"
                        >
                          <Info size={16} className="text-zinc-400" />
                        </button>
                        <div 
                          onClick={() => setDetailModal({ isOpen: true, entity: bike, type: 'bike' })}
                          className="cursor-pointer"
                        >
                          {bike.image_url ? (
                            <img
                              src={bike.image_url}
                              alt={bike.name}
                              className="w-full h-32 sm:h-48 object-cover mb-3 sm:mb-4"
                            />
                          ) : (
                            <div className="w-full h-32 sm:h-48 bg-zinc-800 flex items-center justify-center mb-3 sm:mb-4 font-mono font-bold text-3xl sm:text-5xl">
                              #{bike.number}
                            </div>
                          )}
                          <h3 className="font-heading font-bold text-base sm:text-xl mb-1 truncate">{bike.name}</h3>
                          <div className="mt-2 sm:mt-3 pt-2 sm:pt-3 border-t border-zinc-700">
                            <p className="font-mono text-sm sm:text-lg font-bold text-secondary">{(bike.price || 0).toLocaleString()} CR</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="tuners" className="bg-card border border-zinc-800">
              <AccordionTrigger className="px-6 py-4 hover:no-underline" data-testid="accordion-moto-tuners">
                <div className="flex items-center justify-between w-full pr-4">
                  <h2 className="font-heading font-bold text-3xl">
                    SELECT <span className="text-secondary">TUNER</span>
                  </h2>
                  <span className="font-mono text-xl text-zinc-400">
                    {selectedTuner ? '1' : '0'}/1
                  </span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-3 sm:px-6 pb-6">
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-3 sm:gap-4 pt-4">
                  {tuners.map((tuner) => {
                    const isSelected = selectedTuner === tuner.tuner_id;
                    return (
                      <div
                        key={tuner.tuner_id}
                        data-testid={`moto-tuner-card-${tuner.tuner_id}`}
                        className={`relative bg-zinc-900 border-2 transition-all ${
                          isSelected ? 'border-secondary' : 'border-zinc-800 hover:border-zinc-600'
                        } p-3 sm:p-6`}
                        style={isSelected ? { boxShadow: '0 0 20px rgba(0, 255, 135, 0.3)' } : {}}
                      >
                        {isSelected && (
                          <div className="absolute top-2 right-2 bg-secondary rounded-full p-1">
                            <Check size={20} className="text-black" />
                          </div>
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setDetailModal({ isOpen: true, entity: tuner, type: 'moto_tuner' });
                          }}
                          className="absolute top-2 left-2 bg-zinc-800 hover:bg-zinc-700 rounded-full p-1.5 transition-colors"
                          title="View Details"
                        >
                          <Info size={16} className="text-zinc-400" />
                        </button>
                        <div 
                          onClick={() => setDetailModal({ isOpen: true, entity: tuner, type: 'moto_tuner' })}
                          className="cursor-pointer"
                        >
                          {tuner.image_url ? (
                            <img
                              src={tuner.image_url}
                              alt={tuner.name}
                              className="w-full h-32 sm:h-48 object-cover mb-3 sm:mb-4"
                            />
                          ) : (
                            <div className="w-full h-32 sm:h-48 bg-zinc-800 flex items-center justify-center mb-3 sm:mb-4 font-bold text-3xl sm:text-4xl">
                              {tuner.name.charAt(0)}
                            </div>
                          )}
                          <h3 className="font-heading font-bold text-base sm:text-xl truncate">{tuner.name}</h3>
                          <div className="mt-2 sm:mt-3 pt-2 sm:pt-3 border-t border-zinc-700">
                            <p className="font-mono text-sm sm:text-lg font-bold text-secondary">{(tuner.price || 0).toLocaleString()} CR</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>

        <div className="mt-12 flex justify-center">
          <button
            onClick={handleSubmit}
            data-testid="save-moto-team-button"
            className="btn-skew bg-secondary text-black px-12 py-4 font-heading font-bold text-xl uppercase tracking-wider"
            style={{ boxShadow: '0 0 20px rgba(0, 255, 135, 0.3)' }}
          >
            <span>{hasExistingSelection ? 'Update Team (-50pts)' : 'Save Team'}</span>
          </button>
        </div>
      </div>

      {/* Entity Detail Modal */}
      <EntityDetailModal
        entity={detailModal.entity}
        type={detailModal.type}
        isOpen={detailModal.isOpen}
        onClose={() => setDetailModal({ isOpen: false, entity: null, type: null })}
        isSelected={
          detailModal.type === 'rider' ? selectedRiders.includes(detailModal.entity?.rider_id) :
          detailModal.type === 'bike' ? selectedBikes.includes(detailModal.entity?.bike_id) :
          detailModal.type === 'moto_tuner' ? selectedTuner === detailModal.entity?.tuner_id : false
        }
        onSelect={() => {
          if (detailModal.type === 'rider') toggleRider(detailModal.entity.rider_id);
          else if (detailModal.type === 'bike') toggleBike(detailModal.entity.bike_id);
          else if (detailModal.type === 'moto_tuner') selectTuner(detailModal.entity.tuner_id);
        }}
      />

      {/* Name Your Team Modal (post-save) */}
      <Dialog open={showNameModal} onOpenChange={() => {}}>
        <DialogContent className="bg-zinc-950 border border-zinc-800 max-w-md" onPointerDownOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="font-heading font-bold text-2xl text-center">
              Name Your <span className="text-secondary">Team</span>
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-zinc-400 text-center mb-4">Give your moto team a name!</p>
          <input
            type="text"
            value={teamName}
            onChange={(e) => setTeamName(e.target.value.slice(0, 30))}
            placeholder="Enter team name..."
            data-testid="post-save-moto-team-name-input"
            className="w-full bg-zinc-900 border border-zinc-700 px-4 py-3 text-white font-heading font-bold text-lg focus:border-secondary focus:outline-none transition-colors placeholder:text-zinc-600"
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter' && teamName.trim()) {
                axios.put(`${BACKEND_URL}/api/team-name?league=moto&team_name=${encodeURIComponent(teamName.trim())}`, {}, { withCredentials: true })
                  .then(() => { toast.success('Team named!'); navigate('/dashboard'); })
                  .catch(() => navigate('/dashboard'));
              }
            }}
          />
          <p className="text-xs text-zinc-600">{teamName.length}/30 characters</p>
          <div className="flex gap-3 mt-2">
            <button
              onClick={() => {
                if (teamName.trim()) {
                  axios.put(`${BACKEND_URL}/api/team-name?league=moto&team_name=${encodeURIComponent(teamName.trim())}`, {}, { withCredentials: true })
                    .then(() => { toast.success('Team named!'); navigate('/dashboard'); })
                    .catch(() => navigate('/dashboard'));
                }
              }}
              disabled={!teamName.trim()}
              data-testid="save-moto-team-name-btn"
              className={`flex-1 py-3 font-heading font-bold uppercase ${teamName.trim() ? 'bg-secondary text-black hover:opacity-80' : 'bg-zinc-800 text-zinc-600 cursor-not-allowed'} transition-all`}
            >
              Save Name
            </button>
            <button
              onClick={() => navigate('/dashboard')}
              data-testid="skip-moto-team-name-btn"
              className="px-6 py-3 font-heading font-bold uppercase bg-zinc-800 text-zinc-400 hover:bg-zinc-700 transition-all"
            >
              Skip
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
    </PageBackground>
  );
}
